import { IBranch } from '@/interfaces/IBranch';
import { ICaja } from '@/interfaces/ICaja';
import { ICurrency } from '@/interfaces/ICurrency';
import { IRestaurant } from '@/interfaces/IRestaurant';
import { computed, ref, shallowRef, watch } from 'vue';
import { useLocalStorage } from '@vueuse/core';
import { defineStore } from 'pinia';
import { v4 as uuidv4 } from 'uuid';
import { ITable } from '@/interfaces/ITable';
import ICustomField from '@/interfaces/ICustomField';
import { IDeliveryZone } from '@/interfaces/IDeliveryZone';
import { IInvoicingData } from '@/interfaces/IInvoicingData';
import { timeInsideHours } from '@/utils';
import { COUNTRIES_MAP } from '@/const/countries.const';
import {
  getEffectiveTier,
  isPlanExpired,
} from '@/utils/billing-entitlements.utils';
import BillingService, {
  IBillingSubscriptionSummaryResponse,
} from '@/api/services/billing.service';
import { TBillingSubscriptionLoadStatus } from '@/utils/plan-notice.utils';
import PrintStationsService from '@/api/services/print-stations.service';
import { IPrintStation } from '@/interfaces/IPrintStation';
import SectorsService from '@/api/services/sectors.service';
import { ISector } from '@/interfaces/ISector';

export const DEFAULT_PRINT_STATIONS: IPrintStation[] = [
  { id: null, stationId: 1, name: 'Barra', index: 0 },
  { id: null, stationId: 2, name: 'Cocina', index: 1 },
];

export const useRestaurantStore = defineStore('restaurant', () => {
  const restaurant = ref<IRestaurant | null>(null);
  const branches = shallowRef<IBranch[] | null>(null);
  const visibleBranches = computed(
    () => branches.value?.filter((b) => b.active) ?? null
  );
  const currency = shallowRef<ICurrency | null>(null);
  const selectedBranch = ref<IBranch | null>(null);
  const cajas = shallowRef<ICaja[] | null>(null);
  const caja = shallowRef<ICaja | null>(null);
  const customFields = shallowRef<ICustomField[] | null>(null);
  const tables = shallowRef<ITable[] | null>(null);
  const sectors = shallowRef<ISector[] | null>(null);
  const persistedDeviceId = useLocalStorage('deviceId', uuidv4());
  const deviceId = ref(persistedDeviceId.value);
  const deliveryZones = shallowRef<IDeliveryZone[] | null>(null);
  const loadingBranchId = ref<number | null>(null);
  const invoicingData = shallowRef<IInvoicingData | null>(null);
  const printStations = shallowRef<IPrintStation[]>(DEFAULT_PRINT_STATIONS);
  const printStationsRequestSeq = ref(0);

  const billingService = new BillingService();

  const billingSubscriptionSummary = ref<IBillingSubscriptionSummaryResponse | null>(
    null
  );
  const billingSubscriptionRestaurantId = ref<number | null>(null);
  const billingSubscriptionStatus = ref<TBillingSubscriptionLoadStatus>('idle');
  const billingSubscriptionLoadedAtMs = ref<number | null>(null);
  const billingSubscriptionRequestSeq = ref(0);

  function setCajas(val: ICaja[] | null) {
    cajas.value = val;
  }

  function setCaja(val: ICaja | null) {
    caja.value = val;
  }

  function setCustomFields(val: ICustomField[] | null) {
    customFields.value = val;
  }

  function setRestaurant(val: IRestaurant | null) {
    restaurant.value = val;
  }

  function setBranches(val: IBranch[] | null) {
    // Note: branches should already be filtered by permissions before being set
    branches.value = val;
  }

  function setSelectedBranch(val: IBranch | null) {
    selectedBranch.value = val;
  }

  function setCurrency(val: ICurrency | null) {
    currency.value = val;
  }

  function setTables(val: ITable[] | null) {
    tables.value = val;
  }

  function setSectors(val: ISector[] | null) {
    sectors.value = val;
  }

  async function loadSectors(): Promise<ISector[] | null> {
    const restaurantId = restaurant.value?.id ?? null;
    const branchId = selectedBranch.value?.id ?? null;
    if (!restaurantId || !branchId) {
      sectors.value = null;
      return null;
    }

    const list = await SectorsService.getAll(restaurantId, branchId);

    // Ignore stale responses (restaurant/branch switched while loading).
    if (
      (restaurant.value?.id ?? null) !== restaurantId ||
      (selectedBranch.value?.id ?? null) !== branchId
    ) {
      return sectors.value;
    }

    sectors.value = list;
    return list;
  }

  function setDeliveryZones(val: IDeliveryZone[] | null) {
    deliveryZones.value = val;
  }

  function setLoadingBranchId(val: number | null) {
    loadingBranchId.value = val;
  }

  function setInvoicingData(val: IInvoicingData | null) {
    invoicingData.value = val;
  }

  async function loadPrintStations(): Promise<IPrintStation[]> {
    const restaurantId = restaurant.value?.id ?? null;
    if (!restaurantId) {
      printStations.value = DEFAULT_PRINT_STATIONS;
      return printStations.value;
    }

    const seq = (printStationsRequestSeq.value += 1);

    try {
      const stations = await PrintStationsService.getAll(restaurantId);

      // Ignore stale responses (restaurant switch or newer request in-flight).
      if (printStationsRequestSeq.value !== seq) return printStations.value;
      if ((restaurant.value?.id ?? null) !== restaurantId)
        return printStations.value;

      printStations.value = stations;
    } catch (e) {
      // Printing must never break: keep the current list (last known good for
      // this restaurant, or the reserved defaults set on restaurant switch).
      console.error('Error loading print stations', e);
    }

    return printStations.value;
  }

  function stationName(stationId: number): string {
    const station = printStations.value.find((s) => s.stationId === stationId);
    if (station) return station.name;
    if (stationId === 1) return 'Barra';
    if (stationId === 2) return 'Cocina';
    return `Estación ${stationId}`;
  }

  function resetBillingSubscriptionState() {
    billingSubscriptionSummary.value = null;
    billingSubscriptionRestaurantId.value = null;
    billingSubscriptionStatus.value = 'idle';
    billingSubscriptionLoadedAtMs.value = null;
  }

  async function loadBillingSubscriptionSummary(input?: {
    force?: boolean;
  }): Promise<IBillingSubscriptionSummaryResponse | null> {
    const restaurantId = restaurant.value?.id ?? null;
    if (!restaurantId) {
      resetBillingSubscriptionState();
      return null;
    }

    const force = input?.force === true;
    const ttlMs = 5 * 60 * 1000;
    const nowMs = Date.now();

    if (
      !force &&
      billingSubscriptionStatus.value === 'loaded' &&
      billingSubscriptionRestaurantId.value === restaurantId &&
      billingSubscriptionLoadedAtMs.value != null &&
      nowMs - billingSubscriptionLoadedAtMs.value < ttlMs
    ) {
      return billingSubscriptionSummary.value;
    }

    const seq = (billingSubscriptionRequestSeq.value += 1);

    billingSubscriptionStatus.value = 'loading';
    billingSubscriptionRestaurantId.value = restaurantId;

    try {
      const summary = await billingService.getSubscription(restaurantId);

      // Ignore stale responses (restaurant switch or newer request in-flight).
      if (billingSubscriptionRequestSeq.value !== seq) return null;
      if ((restaurant.value?.id ?? null) !== restaurantId) return null;

      billingSubscriptionSummary.value = summary;
      billingSubscriptionStatus.value = 'loaded';
      billingSubscriptionLoadedAtMs.value = Date.now();
      return summary;
    } catch (e) {
      if (billingSubscriptionRequestSeq.value !== seq) return null;
      if ((restaurant.value?.id ?? null) !== restaurantId) return null;

      // Keep last known good summary for this restaurant (if any),
      // but mark state as error so callers can choose fallbacks.
      if (billingSubscriptionRestaurantId.value !== restaurantId) {
        billingSubscriptionSummary.value = null;
      }

      billingSubscriptionStatus.value = 'error';
      billingSubscriptionLoadedAtMs.value = Date.now();
      return null;
    }
  }

  const restaurantSettings = computed(() => restaurant.value?.settings);
  const effectiveTier = computed(() => getEffectiveTier(restaurant.value));
  const planExpired = computed(() =>
    isPlanExpired(restaurant.value?.planExpiresAt)
  );

  const subscriptionAutoRenews = computed(() => {
    const sub = billingSubscriptionSummary.value;
    if (!sub) return false;
    return sub.status === 'ACTIVE' && !sub.cancelAtPeriodEnd;
  });

  const subscriptionEndsAtPeriodEnd = computed(() => {
    const sub = billingSubscriptionSummary.value;
    if (!sub) return false;
    return sub.status === 'ACTIVE' && sub.cancelAtPeriodEnd;
  });

  const subscriptionIsCancelled = computed(() => {
    const sub = billingSubscriptionSummary.value;
    if (!sub) return false;
    return sub.status === 'CANCELLED';
  });

  const isBranchOpen = computed(() => {
    const branch = selectedBranch.value;

    if (!branch?.displayHours) return true;

    // Get timezone from branch location, or fallback to restaurant country timezone
    const tz =
      branch.location?.tz ??
      COUNTRIES_MAP.get(restaurant.value?.country ?? 'AR')?.tz ??
      'America/Argentina/Buenos_Aires';

    if (!branch.hourRanges || !branch.hourRanges.length) return true;

    return timeInsideHours(branch.hourRanges, tz);
  });

  watch(
    () => restaurant.value?.id ?? null,
    (newRestaurantId) => {
      // Invalidate any in-flight request and reset per-restaurant state.
      billingSubscriptionRequestSeq.value += 1;
      billingSubscriptionLoadedAtMs.value = null;

      if (!newRestaurantId) {
        resetBillingSubscriptionState();
        return;
      }

      billingSubscriptionSummary.value = null;
      billingSubscriptionRestaurantId.value = newRestaurantId;
      billingSubscriptionStatus.value = 'loading';

      void loadBillingSubscriptionSummary({ force: true });
    },
    { immediate: true }
  );

  watch(
    () => restaurant.value?.id ?? null,
    (newRestaurantId) => {
      // Invalidate in-flight requests and reset to defaults until loaded.
      printStationsRequestSeq.value += 1;
      printStations.value = DEFAULT_PRINT_STATIONS;

      if (!newRestaurantId) return;

      void loadPrintStations();
    },
    { immediate: true }
  );

  return {
    restaurant,
    setRestaurant,
    branches,
    visibleBranches,
    setBranches,
    selectedBranch,
    setSelectedBranch,
    customFields,
    setCustomFields,
    currency,
    setCurrency,
    cajas,
    setCajas,
    caja,
    setCaja,
    deviceId,
    tables,
    setTables,
    sectors,
    setSectors,
    loadSectors,
    deliveryZones,
    setDeliveryZones,
    loadingBranchId,
    setLoadingBranchId,
    invoicingData,
    setInvoicingData,
    printStations,
    loadPrintStations,
    stationName,
    restaurantSettings,
    effectiveTier,
    planExpired,
    isBranchOpen,
    billingSubscriptionSummary,
    billingSubscriptionStatus,
    billingSubscriptionLoadedAtMs,
    loadBillingSubscriptionSummary,
    subscriptionAutoRenews,
    subscriptionEndsAtPeriodEnd,
    subscriptionIsCancelled,
  };
});
